import React from 'react';
import { useTranslation } from '~/shared/utils/translation';

import ProfileHeader from '../ProfileHeader/ProfileHeader';
import PointsMeterBar from '../PointsMeterBar/PointsMeterBar';
import BonusPointsList from '../../components/BonusPointsList/BonusPointsList';
import BonusPointsHistory from '../BonusPointsHistory/BonusPointsHistory';
import { useMemberAccountInformation } from '~/shared/hooks/loyalty/queries/useMemberAccountInformation';
import { useMemberRewards } from '~/shared/hooks/loyalty/queries/useMemberRewards';
import { usePointsList } from '~/shared/hooks/loyalty/queries/usePointsList';
import {
    mapRewardToBonusReward,
    mapTransactionToHistoryItem,
    mapAccountInfoToPointsData,
} from '../../lib/loyaltyDataMappers';

import styles from './ProfileTabs.module.scss';

const BonusRewards: React.FC = () => {
    const { translate } = useTranslation();
    console.log('BonusRewards component rendering...');

    // Fetch account information (points, targets, etc.)
    // NOTE: enabled: true forces the query to run even if not a Plus member (for testing)
    const {
        data: accountInfo,
        isLoading: isLoadingAccount,
        error: errorAccount,
    } = useMemberAccountInformation();

    // Fetch member rewards (bonus cards earned from points)
    const {
        data: rewardsData,
        isLoading: isLoadingRewards,
        error: errorRewards,
    } = useMemberRewards();

    // Fetch points history (transactions)
    const {
        data: pointsHistory,
        isLoading: isLoadingHistory,
        error: errorHistory,
    } = usePointsList();

    // Debug logging
    React.useEffect(() => {
        console.log('BonusRewards Debug:', {
            accountInfo,
            rewardsData,
            pointsHistory,
            isLoadingAccount,
            isLoadingRewards,
            isLoadingHistory,
            errorAccount,
            errorRewards,
            errorHistory,
        });
    }, [
        accountInfo,
        rewardsData,
        pointsHistory,
        isLoadingAccount,
        isLoadingRewards,
        isLoadingHistory,
        errorAccount,
        errorRewards,
        errorHistory,
    ]);

    // Handle loading states
    const isLoading = isLoadingAccount || isLoadingRewards || isLoadingHistory;
    const hasError = errorAccount || errorRewards || errorHistory;

    // Transform API data to UI format
    const pointsData = mapAccountInfoToPointsData(accountInfo);

    const memberAccount = accountInfo?.value?.[0];
    const savedUpAmount = memberAccount?.sumOfCoupons;

    const bonusRewards = rewardsData?.value?.map(mapRewardToBonusReward) || [];

    const pointsHistoryItems = React.useMemo(() => {
        return (
            pointsHistory?.value?.map(mapTransactionToHistoryItem).sort((a, b) => {
                const dateA = new Date(a.date);
                const dateB = new Date(b.date);
                return dateB.getTime() - dateA.getTime();
            }) || []
        );
    }, [pointsHistory]);

    const Heading = () => {
        return (
            <ProfileHeader
                title={translate('loyalty.bonusRewards.title')}
                subtitle={
                    hasError || isLoading
                        ? translate('loyalty.bonusRewards.loading')
                        : translate('loyalty.bonusRewards.description')
                }
                earnnedPoints={savedUpAmount}
            />
        );
    };

    return (
        <div className={styles.tabContent}>
            <div className={styles.inner}>
                <Heading />
                <section className={styles.rewardsSection}>
                    <BonusPointsList
                        rewards={bonusRewards}
                        isLoading={isLoadingRewards}
                        hasError={!!errorRewards}
                    />
                </section>
                <section className={styles.pointsSection}>
                    <PointsMeterBar
                        currentPoints={pointsData.currentPoints}
                        targetPoints={pointsData.targetPoints}
                        remainingPoints={pointsData.targetPoints - pointsData.currentPoints}
                        rewardAmount={pointsData.rewardAmount}
                    />
                </section>
                <section className={styles.historySection}>
                    <BonusPointsHistory
                        items={pointsHistoryItems}
                        targetPoints={pointsData.targetPoints}
                        rewardAmount={pointsData.rewardAmount}
                        daysAvailable={30}
                    />
                </section>
            </div>
        </div>
    );
};

export default BonusRewards;
